import { useEffect, useRef, useCallback, useState } from 'react';

// HTTP API: localhost:8000 (WSGI)
// WebSocket: localhost:8001 (ASGI uvicorn — отдельно из-за Python 3.14 + Django middleware несовместимости)
const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8001';
const RECONNECT_DELAY = 3000;
const MAX_RECONNECT = 10;

/**
 * Хук для подключения к WebSocket заказов.
 * Автоматически переподключается при разрыве.
 *
 * @param {Function} onMessage - колбэк при получении события { type, order?, order_id? }
 * @returns {{ connected: boolean, reconnecting: boolean }}
 */
export function useOrdersWebSocket(onMessage) {
  const wsRef = useRef(null);
  const reconnectCount = useRef(0);
  const reconnectTimer = useRef(null);
  const onMessageRef = useRef(onMessage);
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  // Обновляем ref без пересоздания подключения
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);

  const connect = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    const url = `${WS_URL}/ws/admin/orders/?token=${token}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      setReconnecting(false);
      reconnectCount.current = 0;
    };

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type !== 'pong') {
          onMessageRef.current?.(data);
        }
      } catch {}
    };

    ws.onclose = () => {
      setConnected(false);
      if (reconnectCount.current < MAX_RECONNECT) {
        reconnectCount.current += 1;
        setReconnecting(true);
        reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY);
      } else {
        setReconnecting(false);
      }
    };

    ws.onerror = () => ws.close();
  }, []);

  useEffect(() => {
    connect();

    // Ping каждые 30 сек чтобы держать соединение живым
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      clearTimeout(reconnectTimer.current);
      clearInterval(pingInterval);
      wsRef.current?.close();
    };
  }, [connect]);

  return { connected, reconnecting };
}
